import type { PlayerState } from '@crowdset/domain';
import type { PlayerAdapter, PlayerEvents } from './types';

/* Minimal typings for the parts of the official IFrame Player API we use. */
interface YTPlayer {
  loadVideoById(o: { videoId: string; startSeconds?: number }): void;
  cueVideoById(o: { videoId: string; startSeconds?: number }): void;
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getPlayerState(): number;
  setVolume(v: number): void;
  mute(): void;
  unMute(): void;
  destroy(): void;
}
interface YTNamespace {
  Player: new (
    el: HTMLElement,
    opts: {
      width?: string | number;
      height?: string | number;
      host?: string;
      playerVars?: Record<string, string | number>;
      events?: Record<string, (e: { data: number; target: YTPlayer }) => void>;
    },
  ) => YTPlayer;
}
declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YTNamespace> | null = null;

/** Load https://www.youtube.com/iframe_api exactly once. */
export function loadYouTubeApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<YTNamespace>((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) resolve(window.YT);
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    s.onerror = () => {
      apiPromise = null;
      reject(
        new Error(
          'Could not load the YouTube player. A blocker or network policy may be preventing it.',
        ),
      );
    };
    document.head.appendChild(s);
  });
  return apiPromise;
}

const STATE: Record<number, PlayerState> = {
  [-1]: 'unstarted',
  0: 'ended',
  1: 'playing',
  2: 'paused',
  3: 'buffering',
  5: 'cued',
};

export class YouTubeAdapter implements PlayerAdapter {
  readonly kind = 'youtube' as const;
  private player: YTPlayer | null = null;
  private ready = false;
  private queued: (() => void)[] = [];
  private lastState: PlayerState = 'unstarted';

  /**
   * @param frameWindow When set, the player lives in that same-origin window (the pop-out's
   *   player-frame.html) and uses that window's copy of the IFrame API.
   */
  constructor(private readonly frameWindow?: Window & { ytReady?: Promise<YTNamespace> }) {}

  async mount(el: HTMLElement, events: PlayerEvents): Promise<void> {
    const YT = this.frameWindow?.ytReady ? await this.frameWindow.ytReady : await loadYouTubeApi();
    const target = el.ownerDocument.createElement('div');
    el.replaceChildren(target);
    this.player = new YT.Player(target, {
      width: '100%',
      height: '100%',
      playerVars: {
        origin: window.location.origin,
        playsinline: 1,
        controls: 1,
        rel: 0,
        enablejsapi: 1,
      },
      events: {
        onReady: () => {
          this.ready = true;
          for (const fn of this.queued.splice(0)) fn();
          events.onReady();
        },
        onStateChange: (e) => {
          this.lastState = STATE[e.data] ?? 'unstarted';
          events.onState(this.lastState);
        },
        onError: (e) => events.onError(e.data),
        onAutoplayBlocked: () => events.onAutoplayBlocked(),
      },
    });
  }

  private run(fn: (p: YTPlayer) => void) {
    if (this.player && this.ready) fn(this.player);
    else this.queued.push(() => this.player && fn(this.player));
  }

  load(videoId: string, startMs: number, autoplay: boolean): void {
    const startSeconds = Math.max(0, startMs / 1000);
    this.run((p) =>
      autoplay
        ? p.loadVideoById({ videoId, startSeconds })
        : p.cueVideoById({ videoId, startSeconds }),
    );
  }
  play() {
    this.run((p) => p.playVideo());
  }
  pause() {
    this.run((p) => p.pauseVideo());
  }
  stop() {
    this.run((p) => p.stopVideo());
  }
  seek(ms: number) {
    this.run((p) => p.seekTo(Math.max(0, ms / 1000), true));
  }
  currentTimeMs(): number {
    try {
      return this.player && this.ready ? this.player.getCurrentTime() * 1000 : 0;
    } catch {
      return 0;
    }
  }
  state(): PlayerState {
    return this.lastState;
  }
  setVolume(v: number) {
    this.run((p) => p.setVolume(v));
  }
  setMuted(m: boolean) {
    this.run((p) => (m ? p.mute() : p.unMute()));
  }
  destroy() {
    try {
      this.player?.destroy();
    } catch {
      /* already gone */
    }
    this.player = null;
    this.ready = false;
  }
}
